import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { Button } from '../ui/button';

const COOKIE_CONSENT_KEY = 'trh_cookie_consent';

const texts = {
  en: {
    banner: 'This website uses cookies to ensure you get the best experience.',
    accept: 'Accept All',
    necessary: 'Necessary Only',
    manage: 'Cookie Settings',
    policy: 'Cookie Policy',
    settingsTitle: 'Cookie Preferences',
    settingsDesc: 'Choose which cookies you want to allow. Essential cookies cannot be disabled.',
    essential: 'Essential Cookies',
    essentialDesc: 'Required for the website to function. Cannot be disabled.',
    functional: 'Functional Cookies',
    functionalDesc: 'Language preferences and interface settings.',
    analytics: 'Analytics Cookies',
    analyticsDesc: 'Help us understand how visitors interact with our website.',
    save: 'Save Preferences',
    always: 'Always active',
  },
  es: {
    banner: 'Este sitio web utiliza cookies para garantizar la mejor experiencia.',
    accept: 'Aceptar Todas',
    necessary: 'Solo Necesarias',
    manage: 'Configurar Cookies',
    policy: 'Pol. de Cookies',
    settingsTitle: 'Preferencias de Cookies',
    settingsDesc: 'Elija qué cookies desea permitir. Las cookies esenciales no se pueden desactivar.',
    essential: 'Cookies Esenciales',
    essentialDesc: 'Necesarias para el funcionamiento del sitio web. No se pueden desactivar.',
    functional: 'Cookies Funcionales',
    functionalDesc: 'Preferencias de idioma y configuración de interfaz.',
    analytics: 'Cookies Analíticas',
    analyticsDesc: 'Nos ayudan a entender cómo interactúan los visitantes con nuestro sitio web.',
    save: 'Guardar Preferencias',
    always: 'Siempre activas',
  },
  fr: {
    banner: 'Ce site utilise des cookies pour garantir la meilleure expérience.',
    accept: 'Tout Accepter',
    necessary: 'Nécessaires Uniquement',
    manage: 'Paramètres',
    policy: 'Pol. de Cookies',
    settingsTitle: 'Préférences de Cookies',
    settingsDesc: 'Choisissez les cookies que vous souhaitez autoriser. Les cookies essentiels ne peuvent pas être désactivés.',
    essential: 'Cookies Essentiels',
    essentialDesc: 'Nécessaires au fonctionnement du site web. Ne peuvent pas être désactivés.',
    functional: 'Cookies Fonctionnels',
    functionalDesc: 'Préférences de langue et paramètres d\'interface.',
    analytics: 'Cookies Analytiques',
    analyticsDesc: 'Nous aident à comprendre comment les visiteurs interagissent avec notre site.',
    save: 'Enregistrer',
    always: 'Toujours actifs',
  },
  de: {
    banner: 'Diese Website verwendet Cookies, um die beste Erfahrung zu gewährleisten.',
    accept: 'Alle Akzeptieren',
    necessary: 'Nur Notwendige',
    manage: 'Einstellungen',
    policy: 'Cookie-Richtlinie',
    settingsTitle: 'Cookie-Einstellungen',
    settingsDesc: 'Wählen Sie, welche Cookies Sie zulassen möchten. Wesentliche Cookies können nicht deaktiviert werden.',
    essential: 'Wesentliche Cookies',
    essentialDesc: 'Für die Funktion der Website erforderlich. Können nicht deaktiviert werden.',
    functional: 'Funktionale Cookies',
    functionalDesc: 'Spracheinstellungen und Oberflächeneinstellungen.',
    analytics: 'Analyse-Cookies',
    analyticsDesc: 'Helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.',
    save: 'Speichern',
    always: 'Immer aktiv',
  },
  nl: {
    banner: 'Deze website gebruikt cookies voor de beste ervaring.',
    accept: 'Alles Accepteren',
    necessary: 'Alleen Noodzakelijk',
    manage: 'Instellingen',
    policy: 'Cookiebeleid',
    settingsTitle: 'Cookie-instellingen',
    settingsDesc: 'Kies welke cookies u wilt toestaan. Essentiële cookies kunnen niet worden uitgeschakeld.',
    essential: 'Essentiële Cookies',
    essentialDesc: 'Nodig voor het functioneren van de website. Kunnen niet worden uitgeschakeld.',
    functional: 'Functionele Cookies',
    functionalDesc: 'Taalvoorkeuren en interface-instellingen.',
    analytics: 'Analytische Cookies',
    analyticsDesc: 'Helpen ons te begrijpen hoe bezoekers onze website gebruiken.',
    save: 'Opslaan',
    always: 'Altijd actief',
  },
};

export const CookieConsent = () => {
  const { language } = useLanguage();
  const t = texts[language] || texts.en;
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ functional: true, analytics: true });

  useEffect(() => {
    const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!saved) {
      setVisible(true);
    } else {
      try { setPrefs(JSON.parse(saved)); } catch (e) { console.error('Invalid cookie prefs:', e); }
    }
  }, []);

  const saveConsent = (consent) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setPrefs(consent);
    setVisible(false);
    setShowSettings(false);
  };

  const acceptAll = () => saveConsent({ functional: true, analytics: true });
  const necessaryOnly = () => saveConsent({ functional: false, analytics: false });
  const savePreferences = () => saveConsent(prefs);

  // Expose function globally for "Cookie Settings" link in footer
  useEffect(() => {
    window.openCookieSettings = () => {
      const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (saved) try { setPrefs(JSON.parse(saved)); } catch {}
      setShowSettings(true);
      setVisible(true);
    };
    return () => { delete window.openCookieSettings; };
  }, []);

  if (!visible) return null;

  return (
    <>
      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="cookie-settings-modal">
          <div className="bg-white max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-tight">{t.settingsTitle}</h3>
              <button onClick={() => { setShowSettings(false); if (localStorage.getItem(COOKIE_CONSENT_KEY)) setVisible(false); }} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-slate-600 text-sm">{t.settingsDesc}</p>
              {/* Essential - always on */}
              <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200">
                <div>
                  <div className="font-bold text-slate-900 text-sm">{t.essential}</div>
                  <div className="text-xs text-slate-500">{t.essentialDesc}</div>
                </div>
                <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-1">{t.always}</span>
              </div>
              {/* Functional */}
              <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200">
                <div>
                  <div className="font-bold text-slate-900 text-sm">{t.functional}</div>
                  <div className="text-xs text-slate-500">{t.functionalDesc}</div>
                </div>
                <button
                  onClick={() => setPrefs(p => ({ ...p, functional: !p.functional }))}
                  className={`w-10 h-6 rounded-full transition-colors relative ${prefs.functional ? 'bg-green-600' : 'bg-slate-300'}`}
                  data-testid="toggle-functional"
                >
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${prefs.functional ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
              </div>
              {/* Analytics */}
              <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200">
                <div>
                  <div className="font-bold text-slate-900 text-sm">{t.analytics}</div>
                  <div className="text-xs text-slate-500">{t.analyticsDesc}</div>
                </div>
                <button
                  onClick={() => setPrefs(p => ({ ...p, analytics: !p.analytics }))}
                  className={`w-10 h-6 rounded-full transition-colors relative ${prefs.analytics ? 'bg-green-600' : 'bg-slate-300'}`}
                  data-testid="toggle-analytics"
                >
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${prefs.analytics ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
            <div className="p-5 border-t border-slate-200">
              <Button onClick={savePreferences} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-none uppercase tracking-wider font-bold" data-testid="save-cookie-prefs">
                {t.save}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Banner */}
      {!showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-[55] bg-slate-900 text-white border-t border-slate-700 shadow-2xl" data-testid="cookie-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center space-x-3 flex-1">
                <Cookie className="h-5 w-5 text-green-500 flex-shrink-0" />
                <p className="text-sm text-slate-300">
                  {t.banner}{' '}
                  <Link to="/cookies" className="text-green-400 hover:text-green-300 underline">{t.policy}</Link>
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => setShowSettings(true)} className="text-xs text-slate-400 hover:text-white px-3 py-2 border border-slate-600 transition-colors" data-testid="cookie-manage-btn">
                  {t.manage}
                </button>
                <button onClick={necessaryOnly} className="text-xs text-white px-3 py-2 bg-slate-700 hover:bg-slate-600 border border-slate-600 transition-colors" data-testid="cookie-necessary-btn">
                  {t.necessary}
                </button>
                <button onClick={acceptAll} className="text-xs text-white px-3 py-2 bg-green-600 hover:bg-green-700 font-bold transition-colors" data-testid="cookie-accept-btn">
                  {t.accept}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
